import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import useTeamAccessProfile from "../../hooks/useTeamAccessProfile";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";
import { payrollService } from "../../services/payrollService";

const EMPLOYEE_FIELDS = [
  "id",
  "companyId",
  "name",
  "employeeId",
  "email",
  "role",
  "department",
  "source",
  "hireId",
  "applicantId",
  "status",
  "active",
  "enrolled",
  "recurring",
  "currency",
  "account",
];

export default function useCompanyPayroll() {
  const { currentUser } = useAuth();
  const {
    hasPermission,
    loading: accessLoading,
    companyUID,
    departmentScope,
  } = useTeamAccessProfile();
  const allowed =
    hasPermission("canManagePayroll") && departmentScope !== "specific";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reauthRequired, setReauthRequired] = useState(false);
  const revision = useRef("0");
  const generation = useRef(0);
  const inFlight = useRef(false);
  const identity = `${currentUser?.uid || ""}:${companyUID}:${allowed}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const accept = useCallback((value) => {
    if (
      typeof value?.revision !== "string" ||
      !Array.isArray(value.employees) ||
      !Array.isArray(value.runs)
    ) {
      throw new Error(
        "The payroll response could not be verified. Reload before continuing.",
      );
    }
    revision.current = value.revision;
    setData(value);
    setError("");
    return value;
  }, []);
  const reload = useCallback(
    async (signal) => {
      if (!allowed || !currentUser || !companyUID) return;
      const scope = identityRef.current;
      setLoading(true);
      try {
        const value = await payrollService.load(companyUID, signal);
        if (!signal?.aborted && scope === identityRef.current)
          return accept(value);
      } catch (err) {
        if (!signal?.aborted && scope === identityRef.current) {
          setData(null);
          setError(err.message);
        }
      } finally {
        if (!signal?.aborted && scope === identityRef.current)
          setLoading(false);
      }
    },
    [allowed, currentUser, companyUID, accept],
  );
  useEffect(() => {
    const controller = new AbortController();
    generation.current += 1;
    setData(null);
    setError("");
    revision.current = "0";
    reload(controller.signal);
    return () => {
      controller.abort();
      generation.current += 1;
    };
  }, [reload]);
  const call = useCallback(
    async (operation) => {
      if (!allowed || !currentUser || !companyUID)
        throw new Error("Payroll access is required.");
      if (inFlight.current)
        throw new Error("Wait for the current payroll change to finish.");
      const scope = identityRef.current;
      const epoch = generation.current;
      inFlight.current = true;
      try {
        const value = await operation(revision.current);
        if (scope !== identityRef.current || epoch !== generation.current)
          throw new Error("Your payroll session changed. Reload to continue.");
        return accept(value);
      } catch (err) {
        if (scope === identityRef.current && err.status === 428)
          setReauthRequired(true);
        if (scope === identityRef.current && [401, 403].includes(err.status)) {
          setData(null);
          setError(err.message);
        }
        throw err;
      } finally {
        inFlight.current = false;
      }
    },
    [allowed, currentUser, companyUID, accept],
  );
  const saveWorkspace = useCallback(
    (state) =>
      call((rev) =>
        payrollService.save(companyUID, {
          revision: rev,
          employees: state.employees.map((employee) =>
            Object.fromEntries(
              EMPLOYEE_FIELDS.filter(
                (field) => employee[field] !== undefined,
              ).map((field) => [field, employee[field]]),
            ),
          ),
          draft: {
            started: state.started,
            period: state.period,
            paymentDate: state.paymentDate,
            includedIds:
              state.includedIds ||
              state.employees.filter((e) => e.enrolled).map((e) => e.id),
            excluded: state.excluded || [],
            overrides: state.overrides,
          },
        }),
      ),
    [call, companyUID],
  );
  const downloadMonth = useCallback(
    async (period) => {
      if (!allowed) throw new Error("Payroll access is required.");
      const scope = identityRef.current;
      const month = await payrollService.breakdown(companyUID, period);
      if (scope !== identityRef.current)
        throw new Error("Payroll access changed.");
      // Export is audited and advances the vault revision; obtain the new snapshot.
      await call(() => payrollService.load(companyUID));
      return month;
    },
    [allowed, call, companyUID],
  );
  return {
    companyId: companyUID,
    ...data,
    employees: allowed ? data?.employees || [] : [],
    hires: allowed ? data?.hires || [] : [],
    saveWorkspace,
    connect: (bankId, authorizationCode) =>
      call((rev) =>
        payrollService.connect(companyUID, {
          revision: rev,
          bankId,
          authorizationCode,
        }),
      ),
    disconnect: () => call((rev) => payrollService.disconnect(companyUID, rev)),
    submit: (period, idempotencyKey) =>
      call((rev) =>
        payrollService.submit(companyUID, {
          revision: rev,
          period,
          idempotencyKey,
          confirmed: true,
        }),
      ),
    refreshStatus: (run) => call(() => payrollService.status(companyUID, run)),
    activity: () => payrollService.activity(companyUID),
    downloadMonth,
    reload: () => reload(),
    reauthRequired,
    passwordAuth: currentUser?.providerData?.some(
      (p) => p.providerId === "password",
    ),
    reauthenticate: async (password) => {
      const providers = currentUser?.providerData || [];
      if (providers.some((p) => p.providerId === "password")) {
        await reauthenticateWithCredential(
          currentUser,
          EmailAuthProvider.credential(currentUser.email, password),
        );
      } else if (providers.some((p) => p.providerId === "google.com")) {
        await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
      } else if (providers.some((p) => p.providerId === "microsoft.com")) {
        await reauthenticateWithPopup(
          currentUser,
          new OAuthProvider("microsoft.com"),
        );
      } else {
        throw new Error("Sign out and sign in again to confirm your identity.");
      }
      await currentUser.getIdToken(true);
      setReauthRequired(false);
    },
    error,
    allowed,
    ready: allowed && data !== null,
    loading: accessLoading || loading,
  };
}
